import i18n from "i18next";
import { AsyncForm } from "src/components/common/forms/AsyncForm.tsx";
import { FormTextField } from "src/components/common/forms/fields/FormTextField.tsx";
import { requireRule } from "src/components/common/forms/validation.ts";
import { FormSelectField } from "src/components/common/forms/fields/FormSelectField.tsx";
import { FormAsyncUserSelectField } from "src/components/views/users/FormAsyncUserSelectField.tsx";
import { FormDateField } from "src/components/common/forms/fields/FormDateField.tsx";
import { ProductChangeCategory } from "src/api/generated/erp/db/types/tables/productChangeCategory.ts";
import { ProductChangePackage } from "src/api/generated/erp/db/types/tables/productChangePackage.ts";
import {
	ProductChangeEditApi,
	ProductChangeEditApi_FormInitData,
} from "src/api/generated/erp/productChanges/api/productChangeEditApi.ts";
import { FormCommonProps } from "src/components/common/forms/types.ts";
import { ProductChange } from "../../../../../api/generated/erp/db/types/tables/productChange.ts";
import { ProductChangeRequest } from "src/api/generated/erp/db/types/tables/productChangeRequest.ts";
import { defineAgentUiContextKey } from "src/components/views/ai/assistant/uiContext/agentUiContextKey.ts";

export interface ProductChangeFormProps extends FormCommonProps<number> {
	productChangeId?: number;
	connectToRequests?: ProductChangeRequest[];
}

interface FormValues extends ProductChange {}

export const ProductChangeForm = ({
	productChangeId,
	connectToRequests,
	onCompleted,
	onFormEdited,
}: ProductChangeFormProps) => {
	return (
		<AsyncForm<ProductChangeEditApi_FormInitData, FormValues, number>
			fetch={() =>
				ProductChangeEditApi.getFormInitData({
					productChangeId,
				})
			}
			getDefaultValues={({ productChange, packageOptions }) => {
				const firstRequestToConnect = connectToRequests?.[0];
				return (
					productChange ?? {
						title: firstRequestToConnect?.title ?? "",
						description: firstRequestToConnect?.description ?? "",
						categoryId: firstRequestToConnect?.categoryId ?? undefined,
						packageId: packageOptions[0]?.productChangePackageId ?? undefined,
						responsiblePersonId: firstRequestToConnect?.responsiblePersonId ?? undefined,
					}
				);
			}}
			submit={submit}
			onCompleted={onCompleted}
			onFormEdited={onFormEdited}
			columns={2}
			agentContext={{
				key: defineAgentUiContextKey("erp.productChanges.productChange.form"),
				data: {
					productChangeId: productChangeId ?? null,
					connectToRequestIds: connectToRequests?.map((request) => request.productChangeRequestId) ?? null,
				},
			}}
			render={({ control, data }) => (
				<>
					<FormTextField
						control={control}
						name={"title"}
						label={i18n.t("title")}
						rules={requireRule()}
						spanGridColumns
						autoFocus
					/>
					<FormTextField
						control={control}
						name={"description"}
						label={i18n.t("description")}
						multiline
						minRows={3}
						spanGridColumns
					/>
					<FormSelectField
						control={control}
						name={"categoryId"}
						label={i18n.t("category")}
						options={data.categoryOptions}
						getOptionKey={(o: ProductChangeCategory) => o.productChangeCategoryId}
						getOptionLabel={(o) => o.name}
						disableClearable
						rules={requireRule()}
					/>
					<FormSelectField
						control={control}
						name={"packageId"}
						label={i18n.t("change_package")}
						options={data.packageOptions}
						getOptionKey={(o: ProductChangePackage) => o.productChangePackageId}
						getOptionLabel={(o) => `${o.packageNo} | ${o.name}`}
						rules={productChangeId == null ? requireRule() : undefined}
					/>
					<FormAsyncUserSelectField
						control={control}
						name={"responsiblePersonId"}
						label={i18n.t("responsible_person")}
					/>
					<FormDateField
						control={control}
						name={"plannedCompletionDate"}
						label={i18n.t("planned_completion_date")}
					/>
				</>
			)}
		/>
	);

	async function submit(values: FormValues): Promise<number> {
		if (productChangeId != null) {
			await ProductChangeEditApi.update({ productChange: values });
			return productChangeId;
		} else {
			return ProductChangeEditApi.insert({
				title: values.title,
				description: values.description,
				categoryId: values.categoryId,
				packageId: values.packageId,
				responsiblePersonId: values.responsiblePersonId,
				plannedCompletionDate: values.plannedCompletionDate,
				state: "INITIAL",
				connectToRequestIds: connectToRequests?.map((r) => r.productChangeRequestId),
			});
		}
	}
};
